import { useEffect, useRef } from "react";
import { useIsMutating, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth";

const MUTATION_KEY = ["intervals-operation"];

/** Both Settings cards share a lock; old sessions cannot publish late results. */
export function useSyncTask() {
  const token = useAuthStore(state => state.token);
  const client = useQueryClient();
  const mounted = useRef(false);
  const controller = useRef<AbortController | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; controller.current?.abort(); };
  }, []);
  const mutation = useMutation({ mutationKey: MUTATION_KEY, mutationFn: (task: () => Promise<void>) => task() });
  const busy = useIsMutating({ mutationKey: MUTATION_KEY }) > 0;
  function run(task: (signal: AbortSignal, current: () => boolean) => Promise<void>) {
    if (!mounted.current || client.isMutating({ mutationKey: MUTATION_KEY })) return;
    const active = new AbortController();
    controller.current = active;
    const current = () => mounted.current && !active.signal.aborted && useAuthStore.getState().token === token;
    mutation.mutate(() => task(active.signal, current));
  }
  return { run, busy };
}
